import { useRef, useState } from 'react';
import { Check, DollarSign, Trash2, Undo2, X } from 'lucide-react';
import { Button, cn, formatEtTime, minutesToHm } from '@eo/shared';

import {
  BLOCK_STATE_LABELS,
  CALENDAR_COMPACT_HEIGHT,
  CALENDAR_PX_PER_MINUTE,
  CALENDAR_SOURCE_STYLE,
  MOVE_THRESHOLD_PX,
  TIMELINE_SOURCE_LABELS,
} from './constants';
import { blockDensity, isEntryOnly, snappedDuration, snappedOffset } from './helpers';
import type { CalendarBlockProps } from './types';

export function CalendarBlock({
  row,
  taskTypeName,
  top,
  height,
  column,
  columns,
  busy,
  onConfirm,
  onDismiss,
  onDelete,
  onChangeDuration,
  onToggleBillable,
  onEdit,
  onMove,
  onPreview,
  scrollerRef,
  readOnly,
}: CalendarBlockProps) {
  // The block follows the pointer while the button is down and only reports the
  // new duration on release: one PATCH for one drag, not one per pixel. Where it
  // is headed is reported as it goes, because the blocks it lands beside have
  // to make room for it before the drop, not after.
  const [draggedMinutes, setDraggedMinutes] = useState<number | null>(null);
  const dragFrom = useRef<{
    y: number;
    lastY: number;
    scrollTop: number;
    minutes: number;
  } | null>(null);

  // Moving and resizing are the same gesture from different edges, so they keep
  // the same shape: follow the pointer live, report once on release.
  const moveFrom = useRef<{
    x: number;
    y: number;
    lastX: number;
    lastY: number;
    scrollTop: number;
    moved: boolean;
    offsetMinutes: number;
  } | null>(null);
  const suppressClick = useRef(false);
  const [lifted, setLifted] = useState(false);

  // The wheel moves the day under a pointer that has not moved, so a gesture's
  // travel is the pointer's plus the scroller's, and a scroll re-reads it from
  // the last place the pointer was.
  const stopFollowing = useRef<(() => void) | null>(null);
  const scrolledSince = (scrollTop: number) => (scrollerRef.current?.scrollTop ?? 0) - scrollTop;
  const followScroll = (onScroll: () => void) => {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    scroller.addEventListener('scroll', onScroll);
    stopFollowing.current = () => scroller.removeEventListener('scroll', onScroll);
  };
  const unfollowScroll = () => {
    stopFollowing.current?.();
    stopFollowing.current = null;
  };

  const minutes = draggedMinutes ?? row.durationMinutes;
  const density = blockDensity(height, columns);
  // A quarter hour is a single line; an 8px corner turns it into a pill.
  const radius = height < CALENDAR_COMPACT_HEIGHT ? 'rounded-xs' : 'rounded-8';
  const accentRadius = height < CALENDAR_COMPACT_HEIGHT ? 'rounded-l-xs' : 'rounded-l-8';
  const source = TIMELINE_SOURCE_LABELS[row.kind];
  const style = CALENDAR_SOURCE_STYLE[row.kind];
  const alreadyLogged =
    row.loggedAs && !row.confirmed ? `Already logged in ClickUp as ‘${row.loggedAs.title}’` : null;

  const trackResize = () => {
    const from = dragFrom.current;
    if (from === null) return;
    const moved = (from.lastY - from.y + scrolledSince(from.scrollTop)) / CALENDAR_PX_PER_MINUTE;
    const next = snappedDuration(row.startTs, from.minutes + moved);
    setDraggedMinutes(next);
    onPreview(row, { startTs: row.startTs, durationMinutes: next });
  };

  const onResizeStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (busy) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragFrom.current = {
      y: event.clientY,
      lastY: event.clientY,
      scrollTop: scrollerRef.current?.scrollTop ?? 0,
      minutes: row.durationMinutes,
    };
    setDraggedMinutes(row.durationMinutes);
    followScroll(trackResize);
  };

  const onResizeMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragFrom.current === null) return;
    dragFrom.current.lastY = event.clientY;
    trackResize();
  };

  const onResizeEnd = () => {
    unfollowScroll();
    const from = dragFrom.current;
    dragFrom.current = null;
    const settled = draggedMinutes;
    setDraggedMinutes(null);
    if (from === null || settled === null || settled === from.minutes) {
      onPreview(row, null);
      return;
    }
    onChangeDuration(row, settled);
  };

  // Sideways travel only chooses a column among the blocks sharing the hour, so
  // it is reported with the pointer's position and never becomes a save.
  const trackMove = () => {
    const from = moveFrom.current;
    if (from === null) return;
    const dy = from.lastY - from.y + scrolledSince(from.scrollTop);
    if (!from.moved && Math.hypot(from.lastX - from.x, dy) < MOVE_THRESHOLD_PX) return;
    if (!from.moved) setLifted(true);
    from.moved = true;
    from.offsetMinutes = snappedOffset(row.startTs, dy / CALENDAR_PX_PER_MINUTE);
    onPreview(row, {
      startTs: row.startTs + from.offsetMinutes * 60_000,
      durationMinutes: row.durationMinutes,
      clientX: from.lastX,
    });
  };

  const onMoveStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (busy || readOnly) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    moveFrom.current = {
      x: event.clientX,
      y: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      scrollTop: scrollerRef.current?.scrollTop ?? 0,
      moved: false,
      offsetMinutes: 0,
    };
    followScroll(trackMove);
  };

  const onMovePointer = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (moveFrom.current === null) return;
    moveFrom.current.lastX = event.clientX;
    moveFrom.current.lastY = event.clientY;
    trackMove();
  };

  // A press that travelled is a move, and the click the browser fires after it
  // has to be swallowed or the editor opens on every drop. A press that did not
  // travel is left alone, so the click below opens the editor — which is also
  // what Enter and Space fire, and they send no pointer events at all.
  const onMoveEnd = () => {
    unfollowScroll();
    const from = moveFrom.current;
    moveFrom.current = null;
    if (from === null || !from.moved) return;
    setLifted(false);
    suppressClick.current = true;
    if (from.offsetMinutes === 0) {
      onPreview(row, null);
      return;
    }
    onMove(row, row.startTs + from.offsetMinutes * 60_000);
  };

  const onActivate = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onEdit(row);
  };

  // Placed only where there was room, at its estimate: a time and a length
  // nobody recorded, so the block shows neither as if they had been.
  const unplaced = row.placement === 'slotted' && !row.confirmed;
  const when = unplaced ? 'No time yet' : formatEtTime(row.startTs);
  const length = unplaced ? `est. ${minutesToHm(minutes)}` : minutesToHm(minutes);
  const about = [row.client, taskTypeName].filter(Boolean).join(' · ');
  const state = row.confirmed ? BLOCK_STATE_LABELS.logged : BLOCK_STATE_LABELS.proposed;
  const titleTone = row.confirmed ? 'text-text-primary' : 'text-text-secondary';
  const loggedMark = row.confirmed ? (
    <Check aria-hidden className="mr-1 inline size-3 align-[-2px]" />
  ) : null;

  return (
    <div
      className={cn('absolute pr-2', lifted ? 'z-30' : 'z-10')}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${(column / columns) * 100}%`,
        width: `${100 / columns}%`,
      }}
      data-testid={`calendar-block-${row.id}`}
      data-state={row.confirmed ? 'logged' : 'suggested'}
      // The grid reads a press on empty ground as a new entry being drawn, and
      // this is how it tells its own ground from a block already standing on it.
      data-block
    >
      <div
        // The source is never colour alone: it stays in the title and in the
        // accessible name, so hovering or a screen reader still names it.
        title={[row.title, state, when, length, about, source, alreadyLogged]
          .filter(Boolean)
          .join(' · ')}
        className={cn(
          'group/block relative flex h-full items-center gap-2 overflow-hidden border pl-2 pr-2 text-left',
          radius,
          density === 'full' ? 'py-1' : density === 'compact' ? 'py-0.5' : 'py-0',
          row.confirmed ? style.fill : 'bg-background',
          // Dashed is a proposal and solid is a fact. It is the same distinction
          // the page is for, so it gets the shape of the block as well as a
          // word inside it.
          row.confirmed ? 'border-solid' : 'border-dashed',
          style.edge,
          // The block in hand is lifted off the grid, so it stays the one the
          // eye follows while the blocks around it make room.
          lifted && 'shadow-lg ring-2 ring-ring',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 left-0 w-1',
            accentRadius,
            style.accent,
            !row.confirmed && 'opacity-40',
          )}
        />

        {/* One surface, two gestures: a press that goes nowhere opens the
            editor, and a press that travels moves the block. The threshold is
            what keeps a slightly shaky click from being read as a drag. */}
        <button
          type="button"
          disabled={busy}
          onPointerDown={onMoveStart}
          onPointerMove={onMovePointer}
          onPointerUp={onMoveEnd}
          onPointerCancel={onMoveEnd}
          onClick={onActivate}
          aria-label={`${readOnly ? 'Open' : 'Edit'} ${row.title}`}
          className={cn(
            'absolute inset-0 disabled:cursor-default',
            readOnly ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
          )}
        />

        <div className="pointer-events-none relative ml-1 min-w-0 flex-1 pr-9">
          {density === 'full' ? (
            <>
              <p className={cn('truncate text-xs font-medium', titleTone)}>
                {loggedMark}
                {row.title}
              </p>
              <p className="truncate text-[11px] text-text-tertiary">
                {when} · {length}
                {about ? ` · ${about}` : ''}
              </p>
              <p className="truncate text-[11px] text-text-tertiary">
                {alreadyLogged ?? `${state} · ${source}`}
              </p>
            </>
          ) : density === 'compact' ? (
            <div className="flex min-w-0 items-center gap-2">
              <p className={cn('min-w-0 flex-1 truncate text-xs font-medium', titleTone)}>
                {loggedMark}
                {row.title}
              </p>
              {/* Flush to the block's edge, because the actions no longer sit in
                  the row taking a bite out of it — they fade in over this on
                  hover instead, so nothing moves and nothing overlaps. */}
              <span className="shrink-0 text-[11px] tabular-nums text-text-tertiary transition-opacity group-hover/block:opacity-0">
                {when} · {length}
                {about ? ` · ${about}` : ''}
              </span>
            </div>
          ) : (
            <p className={cn('truncate text-[11px] font-medium leading-4', titleTone)}>
              {loggedMark}
              {row.title}
              {about && <span className="font-normal text-text-tertiary"> · {about}</span>}
            </p>
          )}
        </div>

        {/* One row, so the actions and the billable toggle cannot cover each
            other whether hover or keyboard focus brought the actions in. The
            toggle is a state readout as well as a control, so it stays visible
            in the slot the content's right padding reserves for it. */}
        {!readOnly && (
          <div
            data-block-actions
            className={cn(
              'absolute right-1 z-10 flex items-center gap-0.5',
              density === 'full' ? 'top-1' : 'top-1/2 -translate-y-1/2',
            )}
          >
            <div className="pointer-events-none flex items-center gap-0.5 opacity-0 transition-opacity group-focus-within/block:pointer-events-auto group-focus-within/block:opacity-100 group-hover/block:pointer-events-auto group-hover/block:opacity-100">
              {row.confirmed && isEntryOnly(row) ? (
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={busy || !row.entryId}
                  onClick={() => onDelete(row)}
                  aria-label={`Remove logged entry ${row.title}`}
                >
                  <Trash2 className="size-3.5 text-text-tertiary" />
                </Button>
              ) : row.confirmed ? (
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={busy || !row.entryId}
                  onClick={() => onDelete(row)}
                  aria-label={`Undo confirm ${row.title}`}
                  title="Undo confirm — removes this time from ClickUp"
                >
                  <Undo2 className="size-3.5 text-text-tertiary" />
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => onConfirm(row)}
                    aria-label={`Confirm ${row.title}`}
                  >
                    <Check className="size-3.5 text-text-secondary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => onDismiss(row)}
                    aria-label={`Dismiss ${row.title}`}
                  >
                    <X className="size-3.5 text-text-tertiary" />
                  </Button>
                </>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              disabled={busy}
              onClick={() => onToggleBillable(row)}
              aria-pressed={row.variable}
              aria-label={`Billable ${row.title}`}
              title={
                row.variable
                  ? 'Variable (billable) — click to mark fixed'
                  : 'Fixed (not billable) — click to mark variable'
              }
              className={cn(
                'shrink-0',
                row.variable ? 'bg-success/10 text-success' : 'text-text-tertiary',
              )}
            >
              <DollarSign className={cn('size-3.5', !row.variable && 'opacity-60')} />
            </Button>
          </div>
        )}

        {/* The grab area is the bottom edge, as it is in a calendar. It carries
            the duration and the source as its label so the same control is
            reachable by keyboard and readable by a test, neither of which can
            drag or see a colour. */}
        {!readOnly && (
          <button
            type="button"
            disabled={busy}
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            aria-label={`Duration for ${row.title}`}
            className="absolute inset-x-0 bottom-0 z-10 h-2 cursor-ns-resize touch-none disabled:cursor-default"
          >
            <span className="sr-only">
              {minutesToHm(minutes)} · {source}
            </span>
            <span
              aria-hidden
              className="mx-auto block h-0.5 w-8 rounded-full bg-border opacity-0 transition-opacity group-hover/block:opacity-100"
            />
          </button>
        )}
      </div>
    </div>
  );
}
